/**
 * /coaching/:sessionId — one finished conversation's result.
 *
 * The route used to render the coaching picker and ignore its parameter, so a
 * link to a specific conversation silently showed something else. It now
 * shows that conversation's assessment (GET /coaching/sessions/:id/result,
 * owner-scoped on the server), or says plainly why it cannot, with a way on.
 * A conversation in progress is not resumed here: a live attempt belongs to
 * the session view that started it.
 */

import { Link, useParams } from 'react-router-dom';
import { useGetCoachingResultQuery } from '../store/api/coachingApi';
import { ConversationResults } from '../components/ConversationResults';
import { AddResultToPortfolio } from '../components/portfolio/AddResultToPortfolio';
import { LoadErrorNotice } from '../components/LoadErrorNotice';

export function CoachingSessionResultPage() {
  const { sessionId = '' } = useParams<{ sessionId: string }>();
  const { data: result, isLoading, isError, error, refetch } = useGetCoachingResultQuery(sessionId, {
    skip: !sessionId,
  });

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="coaching-session-result">
      <div>
        <Link to="/coaching" className="text-sm text-primary-600 hover:underline">
          ← Coaching
        </Link>
        <h1 className="mt-1 text-2xl font-bold text-gray-900">Conversation result</h1>
      </div>

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : isError ? (
        <LoadErrorNotice
          error={error}
          onRetry={() => void refetch()}
          failedMessage="This conversation's result could not be loaded."
          testIdPrefix="coaching-result"
          notFound={
            <div className="card p-6" data-testid="coaching-result-not-found">
              <p className="text-gray-700">This conversation was not found.</p>
              <p className="mt-1 text-sm text-gray-500">It may belong to another account, or it no longer exists.</p>
              <Link to="/coaching" className="mt-3 inline-block text-sm font-medium text-primary-600 hover:underline">
                Start a new conversation
              </Link>
            </div>
          }
        />
      ) : result?.status === 'evaluated' ? (
        <>
          <ConversationResults result={result} />
          {result.sessionId && <AddResultToPortfolio sessionId={result.sessionId} />}
        </>
      ) : (
        <div className="card p-6" data-testid="coaching-result-not-evaluated">
          <p className="text-gray-700">This conversation has no assessment.</p>
          <p className="mt-1 text-sm text-gray-500">
            No assessment was recorded for this conversation — for example, it was left before it was finished.
          </p>
          <Link to="/coaching" className="mt-3 inline-block text-sm font-medium text-primary-600 hover:underline">
            Start a new conversation
          </Link>
        </div>
      )}
    </div>
  );
}

export default CoachingSessionResultPage;
